import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import PrintTemplate from './PrintTemplate';
import clsx from 'clsx';
import useProductPage from '@themes/product/useProductPage';
import PrintContainer from './PrintContainer';
const PrintButton = () => {
    const [{ product }] = useProductPage(0, 0, [], null, false, false);
    const [visible, setVisible] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const [printStatus, setPrintStatus] = useState('');
    const printTemplateRef = useRef(null);
    // Create a ref for the status message for screen readers
    const statusRef = useRef(null);
    const handlePrint = useReactToPrint({
        contentRef: printTemplateRef,
        documentTitle: `${product.sku} - ${product.title}`,
        bodyClass: 'print-body',
        ignoreGlobalStyles: true,
        onAfterPrint: () => {
            setIsPrinting(false);
            setPrintStatus('Document sent to printer.');
            // Reset the status message after a few seconds
            setTimeout(() => {
                setPrintStatus('');
            }, 5000);
        },
    });
    const onPrint = (e) => {
        e.preventDefault();
        handlePrint();
    };
    // Handle keyboard interactions
    const onKeyDown = (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            handlePrint();
        }
    };
    const handleOnBarCodeLoaded = useCallback(() => {
        setVisible(true);
        setPrintStatus('Print button is now available');
        // Clear the status after announcement
        setTimeout(() => {
            setPrintStatus('');
        }, 2000);
    }, []);
    return (_jsxs(_Fragment, { children: [_jsx("button", Object.assign({ onClick: onPrint, onKeyDown: onKeyDown, title: 'Print Product', "aria-label": `Print product details for ${product.title}`, "aria-busy": isPrinting, "aria-disabled": !visible || isPrinting, disabled: !visible || isPrinting, className: clsx('p-1 bg-transparent border-none rounded-full cursor-pointer flex items-center justify-center z-20 text-gray-200', {
                    hidden: !visible,
                    'opacity-50': isPrinting,
                }, 
                // Add focus styles for better keyboard navigation
                'focus:outline-2 focus:outline-offset-2 focus:outline-blue-500 focus-visible:ring-2 focus-visible:ring-blue-500') }, { children: isPrinting ? (
                // Show loading indicator when printing
                _jsxs("svg", Object.assign({ className: 'animate-spin h-5 w-5 text-gray-200', xmlns: 'http://www.w3.org/2000/svg', fill: 'none', viewBox: '0 0 24 24' }, { children: [_jsx("circle", { className: 'opacity-25', cx: '12', cy: '12', r: '10', stroke: 'currentColor', strokeWidth: '4' }), _jsx("path", { className: 'opacity-75', fill: 'currentColor', d: 'M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' })] }))) : (_jsxs("svg", Object.assign({ width: '20', height: '20', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: '2', "aria-hidden": 'true' }, { children: [_jsx("path", { d: 'M6 9V2h12v7' }), _jsx("path", { d: 'M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2' }), _jsx("path", { d: 'M6 14h12v8H6z' })] }))) })), printStatus && (_jsx("div", Object.assign({ ref: statusRef, "aria-live": 'polite', className: 'sr-only', role: 'status' }, { children: printStatus }))), _jsx(PrintContainer, { children: _jsx(PrintTemplate, { productData: product, onBarcodeLoaded: handleOnBarCodeLoaded, ref: printTemplateRef }) })] }));
};
export default PrintButton;
